import { useEffect, useMemo, useRef, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { type AvatarUploadRef, showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';
import type { Institution } from 'modules/institutions/core/type';
import { isWrongAge, validMeasurementInput } from 'modules/learners/utils';

import type { Learner, LearnerFormRequest, UserMeasurement } from '../type';

import { getExtFileName, getInputStatus, isValidEmail, randomString, trimObject } from 'helpers';
import { UploadFile } from 'helpers/upload-file';
import { useBoolean, useString } from 'hooks';
import { apiCall, useFetchApiToState } from 'hooks/service';
import { isEmpty, omit } from 'lodash';

import { ERROR_MESSAGE, SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';

import { defaultLearnerForm } from '../constant';
import { useFetchApiClassDetail } from '../hook';
import { formLearnerSchema } from '../schema';

export const useLearnerFormController = () => {
  const navigationTo = useNavigate();

  const location = useLocation();

  const params = useParams();

  const { id } = params;

  const { fetchApiClassDetail } = useFetchApiClassDetail(id ?? '');

  const userProfile = useAuthStore(userProfileSelector);

  const institutionId = userProfile?.institution?.id || '';

  const roleKey = userProfile?.role?.key || '';

  const searchSelect = useString('');

  const institutionState = useFetchApiToState<any, any>({
    url: '/institutions',
    data: {
      page: 1,
      limit: 9999,
    },
    method: 'GET',
  });

  const institutions =
    institutionState.state?.items?.map((elInstitutions: Institution) => ({
      value: elInstitutions.id,
      label: elInstitutions.name,
    })) || [];

  const logoRef = useRef<AvatarUploadRef>(null);

  const logoURL = useString('');

  const [logo, setLogo] = useState<File | null>(null);

  const isErrorAvatar = useBoolean(false);

  const isRemoveLogo = useBoolean(false);

  const isAccount = useBoolean(false);

  const searchSelectMonth = useString('');

  const searchSelectYear = useString('');

  useEffect(() => {
    if (!roleKey) {
      return;
    }

    if (roleKey === RoleKey.BIOLOGIC_ADMIN) {
      institutionState.fetchApi();
    }
  }, [roleKey]);

  useEffect(() => {
    if (!roleKey) {
      return;
    }

    let newInstitutionId = institutionId;

    if (!isEmpty(institutions) && roleKey === RoleKey.BIOLOGIC_ADMIN) {
      newInstitutionId = institutions[0].value;
    }

    setFieldValue('institutionId', newInstitutionId);
  }, [JSON.stringify(institutions), roleKey]);

  const formik = useFormik({
    initialValues: defaultLearnerForm,
    validationSchema: formLearnerSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      let avatar = '';

      if (values.firstName.length + values.lastName.length > 80) {
        showToastMessage('error', 'Maximum of 80 characters for the name');

        return;
      }

      if (logo) {
        const ext = getExtFileName(logo.name);

        avatar = `learner-${randomString(4)}-${Date.now()}${ext}`;

        await UploadFile(logo, avatar);
      }

      const dataRequest: any = {
        institutionId: values.institutionId,
        ...trimObject(omit(values, ['height', 'weight', 'bmi', 'name'])),
        avatar,
      };

      dataRequest.name = `${values.firstName || ''} ${values.lastName || ''}`.trim();

      if (!values.monthOfBirth) {
        dataRequest.monthOfBirth = -1;
      }

      if (!values.yearOfBirth) {
        dataRequest.yearOfBirth = -1;
      }

      if (!values.password) {
        delete dataRequest.password;
      }

      if (
        values.height ||
        values.weight ||
        values.bodyFat ||
        values.restingMetabolism ||
        values.skeletalMuscle ||
        values.visceralFatLevel
      ) {
        dataRequest.measurement = {
          height: values?.height ?? null,
          weight: values?.weight ?? null,
          bmi:
            values?.height && values?.weight
              ? Number(
                  (
                    (Number(values.weight) * 10000) /
                    (Number(values.height) * Number(values.height))
                  ).toFixed(2),
                )
              : null,
          bodyFat: values?.bodyFat ?? null,
          restingMetabolism: values?.restingMetabolism ?? null,
          skeletalMuscle: values?.skeletalMuscle ?? null,
          visceralFatLevel: values?.visceralFatLevel ?? null,
        };
      }

      const result: Learner = await apiCall({
        url: '/users/learner',
        data: dataRequest,
        method: 'POST',
        isLoading: true,
        showError: true,
      });

      if (isEmpty(result?.id)) {
        return;
      }

      if (location.pathname.includes(PATH.LEARNER_ADD)) {
        navigationTo(PATH.LEARNERS);

        showToastMessage('success', 'Added Client successfully!');

        return;
      }

      setValues(defaultLearnerForm);

      const addLearner: { message: string; isSuccess: boolean }[] = await apiCall({
        url: `/classes/${id}/add-members `,
        method: 'PATCH',
        data: {
          memberIds: [result?.id],
          type: RoleKey.LEARNER,
        },
        showError: true,
      });

      if (!isEmpty(addLearner) && addLearner[0].isSuccess) {
        showToastMessage('success', 'Created and added Client to class successfully!');

        fetchApiClassDetail();
      }
    },
  });

  const { values, isSubmitting, isValid, handleSubmit, handleChange, setFieldValue, setValues } =
    formik;

  const newMeasurement = {
    height: values?.height ? String(values.height) : '',
    weight: values?.weight ? String(values.weight) : '',
    bodyFat: values?.bodyFat ? String(values.bodyFat) : '',
    restingMetabolism: values?.restingMetabolism ? String(values.restingMetabolism) : '',
    skeletalMuscle: values?.skeletalMuscle ? String(values.skeletalMuscle) : '',
    visceralFatLevel: values?.visceralFatLevel ? String(values.visceralFatLevel) : '',
  };

  const isValidateMeasurement = useMemo(() => {
    return (
      validMeasurementInput('height', newMeasurement.height) &&
      validMeasurementInput('weight', newMeasurement.weight)
      /* validMeasurementInput('bodyFat', newMeasurement.bodyFat) &&
      validMeasurementInput('skeletalMuscle', newMeasurement.skeletalMuscle) &&
      validMeasurementInput('visceralFatLevel', newMeasurement.visceralFatLevel) &&
      validMeasurementInput('restingMetabolism', newMeasurement.restingMetabolism) */
    );
  }, [JSON.stringify(newMeasurement)]);

  const invalidDateOfBirth = useMemo(() => {
    return Number(values.yearOfBirth) < 1;
  }, [values.yearOfBirth]);

  const emailStatus = getInputStatus(
    String(values.email),
    SUCCESS_MESSAGE.EMAIL,
    isValidEmail(String(values.email).trim()).isValid
      ? ''
      : isValidEmail(String(values.email).trim()).message,
  );

  const disabled =
    isSubmitting ||
    !isValid ||
    !isValidPhoneNumber(values.phone) ||
    emailStatus.helperText === ERROR_MESSAGE.EMAIL ||
    !isValidateMeasurement ||
    invalidDateOfBirth ||
    !values.gender ||
    isWrongAge(values.yearOfBirth!) ||
    (isAccount.value && !values.password) ||
    (values.email ? (isValidEmail(String(values.email).trim()).isValid ? false : true) : false);

  const handleChangePhone = (value: string) => {
    setFieldValue('phone', value || '');
  };

  const onChangeGender = (newGenderKey: string) => () => {
    setFieldValue('gender', newGenderKey);
  };

  const handleChangeDate = (type: keyof LearnerFormRequest, value: number) => {
    switch (type) {
      case 'dayOfBirth':
        setFieldValue(type, value);

        break;

      case 'monthOfBirth':
        setFieldValue(type, value);

        if (!value) {
          setFieldValue('dayOfBirth', null);
        }

        break;

      case 'yearOfBirth':
        setFieldValue(type, value);

        break;

      default:
        return;
    }
  };

  const handleChangeMeasurement = (type: keyof UserMeasurement, text: string) => {
    const newText = text.replace(/,/g, '');
    const decimal = type === 'height' ? 3 : 2;

    if (
      type === 'weight' ||
      type === 'height'
      // type === 'restingMetabolism' ||
      // type === 'visceralFatLevel' ||
      // type === 'bodyFat' ||
      // type === 'skeletalMuscle'
    ) {
      const valueSplit = newText.split('.');

      if (valueSplit.length === 1 || (valueSplit.length > 1 && valueSplit[1].length < decimal)) {
        newMeasurement[type] = newText;
        setFieldValue(type, newText);
      }
    }
  };

  const goBack = () => {
    navigationTo(PATH.LEARNERS);
  };

  const onChangeInstitution = (item: any) => {
    if (!item) {
      return;
    }

    setFieldValue('institutionId', item.value);
  };

  const onChangeSearchInstitution = (item: string) => {
    searchSelect.setValue(item);
  };

  const onChangeAvatar = (file: File) => {
    setLogo(file);
  };

  const onErrorAvatar = (file: File) => {
    isErrorAvatar.setValue(Boolean(file));
  };

  const onDeleteLogo = () => {
    logoRef.current?.clearAvatar();
    setLogo(null);

    if (logoURL.value) {
      isRemoveLogo.setValue(true);
    }
  };

  const onChangeMonths = (item: any) => {
    setFieldValue('monthOfBirth', Number(item.value));
  };

  const onChangeSearchMonths = (value: string) => {
    searchSelectMonth.setValue(value);
  };

  const onChangeYear = (item: any) => {
    setFieldValue('yearOfBirth', Number(item.value));
  };

  const onChangeSearchYears = (value: string) => {
    searchSelectYear.setValue(value);
  };

  return {
    ...values,
    emailStatus,
    handleChange,
    handleSubmit,
    handleChangeMeasurement,
    handleChangePhone,
    onChangeGender,
    disabled,
    handleChangeDate,
    goBack,
    roleKey,
    institutions,
    onChangeInstitution,
    onChangeSearchInstitution,
    logoRef,
    onChangeAvatar,
    logoURL,
    onErrorAvatar,
    isErrorAvatar: isErrorAvatar.value,
    onDeleteLogo,
    logo,
    isAccount,
    searchSelect,
    onChangeMonths,
    onChangeSearchMonths,
    searchSelectMonth,
    onChangeYear,
    onChangeSearchYears,
    searchSelectYear,
  };
};
